import { forwardRef } from 'react';
import {
  RadioGroup as FluentRadioGroup,
  Radio as FluentRadio,
  Field,
  mergeClasses,
} from '@fluentui/react-components';
import type { RadioGroupProps as FluentRadioGroupProps } from '@fluentui/react-components';
import { useRadioGroupStyles } from './RadioGroup.styles';

export interface RadioGroupOption {
  /** Unique value identifier (passed to onChange) */
  value: string;
  /** Display text for this option */
  label: string;
}

export interface RadioGroupProps extends Omit<FluentRadioGroupProps, 'children'> {
  /** Label text displayed above the radio group */
  label?: string;
  /** Shows red asterisk next to label */
  required?: boolean;
  /** Array of radio options rendered in a 2-column grid */
  options: RadioGroupOption[];
  /** Validation message displayed below the radio group */
  validationMessage?: string;
  /** Controls validation message styling */
  validationState?: 'error' | 'warning' | 'success' | 'none';
  /** Optional CSS class merged with component styles */
  className?: string;
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ label, required, options, validationMessage, validationState, className, ...rest }, ref) => {
    const styles = useRadioGroupStyles();

    return (
      <Field
        className={mergeClasses(styles.root, className)}
        label={label}
        required={required}
        validationMessage={validationMessage}
        validationState={validationState}
      >
        <FluentRadioGroup ref={ref} className={styles.radioGroup} {...rest}>
          {options.map((option) => (
            <FluentRadio key={option.value} value={option.value} label={option.label} />
          ))}
        </FluentRadioGroup>
      </Field>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';
